import React, { useState, useEffect } from 'react';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Database, Check } from 'lucide-react';

interface ColumnInfo {
  name: string;
  type: string;
  sampleValues?: string[];
}

interface PassthroughColumnsProps {
  columns: ColumnInfo[];
  onSelect: (selectedColumns: string[]) => void;
  disabled?: boolean;
  selectedColumns?: string[];
}

export const PassthroughColumns: React.FC<PassthroughColumnsProps> = ({ 
  columns, 
  onSelect, 
  disabled,
  selectedColumns = []
}) => {
  const [localSelectedColumns, setLocalSelectedColumns] = useState<string[]>(selectedColumns);

  // Auto-confirm when selection changes
  useEffect(() => {
    if (!disabled) {
      onSelect(localSelectedColumns);
    }
  }, [localSelectedColumns, onSelect, disabled]);

  const handleColumnToggle = (columnName: string) => {
    const newSelection = localSelectedColumns.includes(columnName) 
      ? localSelectedColumns.filter(col => col !== columnName)
      : [...localSelectedColumns, columnName];
    
    setLocalSelectedColumns(newSelection);
  };

  if (disabled && selectedColumns.length > 0) {
    return (
      <div className="border border-slate-600 rounded-lg p-4 bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <span className="font-medium text-white">Passthrough columns selected</span>
          <Badge variant="outline" className="text-green-400 border-green-400">
            <Check className="w-3 h-3 mr-1" />
            Selected
          </Badge>
        </div>
        <div className="space-y-2">
          {selectedColumns.map(column => (
            <div key={column} className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-300">{column}</span>
              <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                Passthrough
              </Badge>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-slate-400">
        Select additional columns you think are important to picking your target accounts and we'll include them in the AI model.
      </p>

      <div className="space-y-3">
        {columns.map((column) => (
          <div key={column.name} className="flex items-center space-x-3 p-3 border border-slate-600 rounded-lg bg-slate-800">
            <Checkbox
              id={`passthrough-${column.name}`}
              checked={localSelectedColumns.includes(column.name)}
              onCheckedChange={() => handleColumnToggle(column.name)}
              disabled={disabled}
            />
            <label htmlFor={`passthrough-${column.name}`} className="flex items-center gap-2 cursor-pointer flex-1 text-white">
              <Database className="w-4 h-4 text-slate-400" />
              <div className="flex-1">
                <span className="font-medium">{column.name}</span>
                <div className="text-xs text-slate-400 mt-1">
                  Type: {column.type}
                  {column.sampleValues && column.sampleValues.length > 0 && (
                    <span className="ml-2">
                      Sample: {column.sampleValues.slice(0, 2).join(', ')}
                      {column.sampleValues.length > 2 && '...'}
                    </span>
                  )}
                </div>
              </div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}; 